var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Renderable } from "../rendering/renderable/Renderable";
import { MeshFacet } from "flavr-common";
import { Reflector } from "three/examples/jsm/objects/Reflector";
import { PlaneGeometry, Vector3, Quaternion, Color } from "three";
import { modelPool } from "@modules/model";
import { meshBoundingService } from "@modules/bounds/MeshBoundingService";
const DEFAULT_MIRROR_WIDTH = 2;
const DEFAULT_MIRROR_HEIGHT = 2;
const CORRECTION_ORIENTATION = new Quaternion().setFromAxisAngle(new Vector3(1, 0, 0), -Math.PI * 0.5);
const tmpMeshSize = new Vector3();
/**
 * Creates a Mirror that creates a plane with the same transformation as the underlying model.
 */
export class MirrorRenderable extends Renderable {
    constructor(owner) {
        super(owner);
        this.owner = owner;
        this.type = MirrorRenderable.typestatic;
        this.color = 0xffffff;
        this.textureWidth = 512;
        this.textureHeight = 512;
        this.clipBias = 0.03;
        this.multisample = 0;
        this.mirrorWidth = DEFAULT_MIRROR_WIDTH;
        this.mirrorHeight = DEFAULT_MIRROR_HEIGHT;
    }
    destroy() {
        var _a;
        if (this.reflectorObject !== undefined) {
            this.owner.removeObjectFromScene(this.reflectorObject);
        }
        (_a = this.reflectorObject) === null || _a === void 0 ? void 0 : _a.dispose();
        this.reflectorObject = undefined;
    }
    setVisible(visible) {
        this.owner.setVisible(visible);
    }
    onInit(facet) {
        this.facet = facet;
        this.color = facet.color;
        this.textureWidth = facet.textureWidth;
        this.textureHeight = facet.textureHeight;
        this.clipBias = facet.clipBias;
        this.multisample = facet.multisample;
        this.facet = undefined;
    }
    updateFacet(facet) {
        return __awaiter(this, void 0, void 0, function* () {
            const { width, height } = yield this.resolveMirrorPlaneSize();
            const needsUpdate = this.color !== facet.color ||
                this.textureWidth !== facet.textureWidth ||
                this.textureHeight !== facet.textureHeight ||
                this.clipBias !== facet.clipBias ||
                this.multisample !== facet.multisample ||
                this.mirrorWidth !== width ||
                this.mirrorHeight !== height ||
                this.facet === undefined;
            if (!needsUpdate)
                return;
            this.color = facet.color;
            this.textureWidth = facet.textureWidth;
            this.textureHeight = facet.textureHeight;
            this.clipBias = facet.clipBias;
            this.multisample = facet.multisample;
            this.mirrorWidth = width;
            this.mirrorHeight = height;
            if (this.reflectorObject === undefined)
                this.createMirrorMesh();
            else
                this.updateMirror();
        });
    }
    updateTransform() {
        if (this.reflectorObject === undefined)
            return;
        const customOrientation = this.owner.worldOrientation.multiply(CORRECTION_ORIENTATION);
        const s = this.owner.worldScale;
        const customScale = new Vector3(s.x, s.z, s.y);
        this.reflectorObject.position.copy(this.owner.worldPosition);
        this.reflectorObject.quaternion.copy(customOrientation);
        this.reflectorObject.scale.copy(customScale);
    }
    createMirrorMesh() {
        const geometry = new PlaneGeometry(this.mirrorWidth, this.mirrorHeight);
        this.reflectorObject = new Reflector(geometry, {
            color: new Color().setHex(this.color),
            textureWidth: this.textureWidth,
            textureHeight: this.textureHeight,
            clipBias: this.clipBias,
            multisample: this.multisample === 0 ? undefined : this.multisample
        });
        this.owner.addObjectToScene(this.reflectorObject);
        this.updateTransform();
        this.reflectorObject.visible = this.owner.visible;
    }
    updateMirror() {
        if (this.reflectorObject === undefined)
            return;
        this.reflectorObject.dispose();
        this.createMirrorMesh();
    }
    resolveMirrorPlaneSize() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const model = modelPool.getModel(this.owner.modelUID);
            const meshFacet = model === null || model === void 0 ? void 0 : model.getFacet(MeshFacet);
            if (meshFacet === undefined) {
                return {
                    width: DEFAULT_MIRROR_WIDTH,
                    height: DEFAULT_MIRROR_HEIGHT
                };
            }
            const meshUrl = meshBoundingService.generateFullMeshUrl(meshFacet.meshName, meshFacet.params.pieceName);
            const meshBounding = yield meshBoundingService.getBoundingOnceFinished(meshUrl);
            if (meshBounding === undefined) {
                return {
                    width: DEFAULT_MIRROR_WIDTH,
                    height: DEFAULT_MIRROR_HEIGHT
                };
            }
            meshBounding.boundingBox.getSize(tmpMeshSize);
            const scaleFactor = (_a = meshFacet.params.vertexScaleFactor) !== null && _a !== void 0 ? _a : 1;
            const width = tmpMeshSize.x * scaleFactor;
            const height = (tmpMeshSize.z !== 0 ? tmpMeshSize.z : tmpMeshSize.y) * scaleFactor;
            return {
                width: width > 0 ? width : DEFAULT_MIRROR_WIDTH,
                height: height > 0 ? height : DEFAULT_MIRROR_HEIGHT
            };
        });
    }
}
MirrorRenderable.typestatic = "Mirror";
